// macOS 권한 상태(화면 기록 · 손쉬운 사용) — 한 곳에서 구독한다.
// "내 기기 정보" 카드(로그인 전 · [연결] 화면)와 로그인 후 모든 화면 위의 경고 띠(`PermissionBanner`)가 같이 쓴다
// (2026-10-12 사용자 지적 — 수퍼유저로 자동 로그인하면 [연결] 화면이 없어 권한이 빠진 것을 볼 곳이 없었다).
import { useEffect, useState } from 'react';
import { invoke } from '@tauri-apps/api/core';
import { listen } from '@tauri-apps/api/event';
import { IS_SHELL } from '../../shell';

/** macOS 권한 상태(에이전트 `RS_PERMS:` → 껍데기 `rs-perms` 이벤트 / `current_perms`). */
export type Perms = {
  /** 화면 기록 — 없으면 상대에게 검은 화면. */
  screen?: boolean;
  /** 손쉬운 사용 — 없으면 원격 조작이 무시된다. */
  input?: boolean;
  /** 에이전트가 실주입 모드인지(dry-run 이면 손쉬운 사용은 필요 없다). */
  inputRequired?: boolean;
};

/** 빠진 권한 한 건 — 화면에 그대로 쓴다. */
export type MissingPerm = { kind: 'screen' | 'input'; label: string; effect: string };

/** 빠진 권한 목록(표시 순서 고정). 🔴 판정 기준은 껍데기 `perms_missing()`(트레이 문구)과 같아야 한다. */
export function missingPerms(p: Perms): MissingPerm[] {
  const out: MissingPerm[] = [];
  if (p.screen === false) {
    out.push({ kind: 'screen', label: '화면 기록', effect: '상대에게 검은 화면이 보입니다' });
  }
  if (p.inputRequired !== false && p.input === false) {
    out.push({ kind: 'input', label: '손쉬운 사용', effect: '상대의 마우스·키보드가 동작하지 않습니다' });
  }
  return out;
}

/**
 * 권한 상태 구독 + 빠진 목록 + 설정 열기·앱 다시 시작. 껍데기가 없거나(브라우저) Windows 면 `missing` 은 늘 빈 배열이다
 * (Windows 에이전트는 `RS_PERMS` 를 보내지 않는다 — 상태가 `undefined` 로 남는다).
 */
export function useMacPerms() {
  // 없으면(Windows·아직 모름) 경고를 그리지 않는다.
  const [perms, setPerms] = useState<Perms | undefined>(undefined);

  useEffect(() => {
    if (!IS_SHELL) return;
    // 🔴 리스너를 달기 전에 온 갱신은 사라진다 → 구독 + 최신값 직접 조회(useMyConnection 의 정체성과 같은 이유).
    const un = listen<Perms>('rs-perms', (e) => setPerms(e.payload));
    invoke<Perms | null>('current_perms')
      .then((v) => {
        if (v) setPerms(v);
      })
      .catch(() => {});
    return () => {
      un.then((f) => f());
    };
  }, []);

  const missing = perms ? missingPerms(perms) : [];
  /** 시스템 설정의 해당 개인정보 보호 항목을 연다. */
  const openSettings = (kind: MissingPerm['kind']) => {
    invoke('open_privacy_settings', { kind }).catch(() => {});
  };
  /** 권한을 켠 뒤 반영이 안 될 때(macOS 는 실행 중 프로세스에 바로 반영하지 않기도 한다). */
  const restartApp = async () => {
    const { relaunch } = await import('@tauri-apps/plugin-process');
    await relaunch();
  };
  return { missing, openSettings, restartApp };
}
